import { useState, useMemo, useCallback, useEffect } from 'react';
import { addWeeks, addMonths } from 'date-fns';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Clock, Lightbulb, SlidersHorizontal, X } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import api from '@/lib/axios';
import { useAuthStore, ROL } from '@/store/auth.store';
import { Calendar, MiniCalendar, type CalendarEvent, type CalendarView } from '@/components/calendar/Calendar';
import { CitaFormDialog } from './CitaFormDialog';
import { CitaDetailDialog } from './CitaDetailDialog';
import { getErrorMessage } from '@/lib/errors';
import { getEstadoCitaInfo } from '@/types/cita';
import { useCapaTareasAgenda } from '@/components/tareas/CapaTareasAgenda';

const ROLES_CITA    = [ROL.SUPER_ADMIN, ROL.MEDICO, ROL.MEDICO_ADMIN, ROL.ASISTENTE, ROL.RECEPCIONISTA, ROL.ADMIN_CLINICA];
const ROLES_CONSULTA = [ROL.SUPER_ADMIN, ROL.MEDICO, ROL.MEDICO_ADMIN];

const PALETTE = ['#0c77a6','#7c3aed','#059669','#d97706','#dc2626','#0891b2','#4338ca','#7c2d12'];

const DIA_MAP: Record<string, number> = {
  domingo: 0, lunes: 1, martes: 2, miercoles: 3, jueves: 4, viernes: 5, sabado: 6,
};

function initials(nombre: string) {
  return nombre.split(' ').map(w => w[0] ?? '').join('').toUpperCase().slice(0, 2);
}

function toLocalDT(d: Date) {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

function citaToEvent(cita: any): CalendarEvent {
  return {
    id: cita.id,
    title: cita.paciente
      ? `${cita.paciente.nombre} ${cita.paciente.apellidoPaterno}`
      : 'Paciente',
    start: new Date(cita.fechaInicio).toISOString(),
    end:   new Date(cita.fechaFin).toISOString(),
    description: [
      cita.medico?.nombre ? `Dr. ${cita.medico.nombre}` : '',
      cita.motivo || '',
    ].filter(Boolean).join(' · '),
    color: getEstadoCitaInfo(cita.estado).hex,
    _cita: cita,
  };
}

function generateBloqueEvents(bloques: any[], rangeStart: Date, rangeEnd: Date): CalendarEvent[] {
  const events: CalendarEvent[] = [];
  for (const b of bloques) {
    if (!b.esBloqueo) continue;
    const dow = DIA_MAP[b.diaSemana];
    if (dow === undefined) continue;
    const cursor = new Date(rangeStart);
    while (cursor.getDay() !== dow) cursor.setDate(cursor.getDate() + 1);
    while (cursor <= rangeEnd) {
      const [sh, sm] = (b.horaInicio as string).split(':').map(Number);
      const [eh, em] = (b.horaFin   as string).split(':').map(Number);
      const start = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate(), sh, sm);
      const end   = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate(), eh, em);
      events.push({
        id: `bloque-${b.id}-${cursor.toISOString().split('T')[0]}`,
        title: 'No disponible',
        start: start.toISOString(),
        end: end.toISOString(),
        color: '#e5e7eb',
        _isBloqueHorario: true,
        _bloque: b,
      });
      cursor.setDate(cursor.getDate() + 7);
    }
  }
  return events;
}

function nextOccurrence(date: Date, recurrencia: string): Date {
  switch (recurrencia) {
    case 'semanal':    return addWeeks(date, 1);
    case 'quincenal':  return addWeeks(date, 2);
    case 'trimestral': return addMonths(date, 3);
    default:           return addMonths(date, 1); // mensual
  }
}

function expandCita(cita: any, rangeStart: Date, rangeEnd: Date): any[] {
  if (!cita.esRecurrente || !cita.recurrencia) return [cita];

  const duration = new Date(cita.fechaFin).getTime() - new Date(cita.fechaInicio).getTime();
  const originalMs = new Date(cita.fechaInicio).getTime();
  const results: any[] = [];

  let cursor = new Date(cita.fechaInicio);
  // advance to first occurrence within range
  while (cursor < rangeStart) cursor = nextOccurrence(cursor, cita.recurrencia);

  let safety = 0;
  while (cursor <= rangeEnd && safety++ < 366) {
    const fi = new Date(cursor);
    const ff = new Date(cursor.getTime() + duration);
    const isOriginal = fi.getTime() === originalMs;
    results.push({
      ...cita,
      id: isOriginal ? cita.id : `${cita.id}_${fi.toISOString().split('T')[0]}`,
      fechaInicio: fi.toISOString(),
      fechaFin:    ff.toISOString(),
      _isVirtualRecurrence: !isOriginal,
    });
    cursor = nextOccurrence(new Date(cursor), cita.recurrencia);
  }
  return results;
}

/* ─── Sidebar sub-components ─── */

const ES_DAYS = ['domingo', 'lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado'] as const;

function MedicoCard({
  medico, color, visible, onToggle, bloques, clinicaHorarios,
}: {
  medico: any;
  color: string;
  visible: boolean;
  onToggle: () => void;
  bloques: any[];
  clinicaHorarios: any[];
}) {
  const todayES = ES_DAYS[new Date().getDay()];

  const u = medico.usuario;
  const displayName = u
    ? [u.nombre, u.apellidoPaterno, u.apellidoMaterno].filter(Boolean).join(' ')
    : medico.nombre;

  // Doctor's explicit available slots for today
  const doctorSlots = bloques.filter(
    b => b.medicoId === medico.id && b.diaSemana === todayES && !b.esBloqueo,
  );
  // Doctor has explicit blocked slots for today (personal overrides)
  const doctorHasAnyConfig = bloques.some(b => b.medicoId === medico.id && b.diaSemana === todayES);

  // If doctor has no explicit config for today → inherit clinic schedule
  const effectiveSlots = doctorHasAnyConfig
    ? doctorSlots
    : clinicaHorarios.filter(h => h.diaSemana === todayES && !h.esBloqueo);

  const schedule = effectiveSlots.length > 0
    ? `${effectiveSlots[0].horaInicio.slice(0, 5)} - ${effectiveSlots[effectiveSlots.length - 1].horaFin.slice(0, 5)}`
    : null;

  return (
    <button
      type="button"
      onClick={onToggle}
      className={cn(
        'w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-opacity hover:bg-gray-50 rounded-xl',
        !visible && 'opacity-35',
      )}
    >
      {/* Avatar */}
      {u?.fotoUrl ? (
        <img
          src={u.fotoUrl}
          alt={displayName}
          className="w-9 h-9 rounded-full object-cover flex-shrink-0 ring-2 ring-white shadow-sm"
        />
      ) : (
        <div
          className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 ring-2 ring-white shadow-sm"
          style={{ backgroundColor: color }}
        >
          {initials(displayName)}
        </div>
      )}

      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold text-text-primary truncate leading-snug">
          {displayName}
        </p>
      </div>

      {/* Schedule */}
      {schedule && (
        <div className="flex-shrink-0 flex items-center gap-1 text-text-secondary">
          <Clock size={10} className="flex-shrink-0" />
          <span className="text-[10px] tabular-nums whitespace-nowrap">{schedule}</span>
        </div>
      )}
      
      
        {medico.especialidad?.nombre && (
          <p className="text-[10px] text-text-secondary truncate leading-tight mt-0.5 block">
            {medico.especialidad.nombre}
          </p>
        )}
    </button>
  );
}

/* ─── Main component ─── */

export function AgendaPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { usuario } = useAuthStore();

  const canManage          = !!(usuario?.rolId && (ROLES_CITA    as string[]).includes(usuario.rolId));
  const canIniciarConsulta = !!(usuario?.rolId && (ROLES_CONSULTA as string[]).includes(usuario.rolId));

  const [currentDate,    setCurrentDate]    = useState(new Date());
  const [view,           setView]           = useState<CalendarView>('week');
  const [hiddenMedicos,  setHiddenMedicos]  = useState<Set<string>>(new Set());
  const [showCanceladas, setShowCanceladas] = useState(false);
  const [formOpen,       setFormOpen]       = useState(false);
  const [formDefaults,   setFormDefaults]   = useState<any>({});
  const [selectedCita,   setSelectedCita]   = useState<any>(null);
  const [editingCita,    setEditingCita]    = useState<any>(null);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const { capa: capaTareas, modal: modalTarea } = useCapaTareasAgenda(currentDate);

  // Forzar vista lista en mobile al montar
  useEffect(() => {
    if (window.innerWidth < 768) setView('agenda');
  }, []);

  const [rangeStart, rangeEnd] = useMemo(() => {
    const s = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
    const e = new Date(currentDate.getFullYear(), currentDate.getMonth() + 2, 0, 23, 59, 59);
    return [s, e];
  }, [currentDate.getFullYear(), currentDate.getMonth()]);

  const { data: citas = [], isLoading: loadingCitas } = useQuery({
    queryKey: ['citas-agenda', rangeStart.toISOString(), rangeEnd.toISOString()],
    queryFn: () =>
      api.get('/api/citas', {
        params: { fechaInicio: rangeStart.toISOString(), fechaFin: rangeEnd.toISOString() },
      }).then(r => r.data),
    enabled: canManage,
  });

  const { data: bloques = [] } = useQuery({
    queryKey: ['bloques-horario'],
    queryFn: () => api.get('/api/bloques-horario').then(r => r.data),
    enabled: canManage,
  });

  const { data: pacientes = [] } = useQuery({
    queryKey: ['pacientes-select'],
    queryFn: () => api.get('/api/pacientes').then(r => r.data),
    enabled: canManage,
  });

  const { data: medicos = [] } = useQuery({
    queryKey: ['medicos-select'],
    queryFn: () => api.get('/api/medicos').then(r => r.data),
    enabled: canManage,
  });

  const { data: clinicas = [] } = useQuery({
    queryKey: ['consultorios'],
    queryFn: () => api.get('/api/clinicas/consultorios').then(r => r.data),
  });

  const clinicaId = usuario?.clinicaId ?? (clinicas as any[])[0]?.id ?? '';

  const { data: clinicaHorarios = [] } = useQuery({
    queryKey: ['clinica-horarios-sidebar', clinicaId],
    queryFn: () => api.get(`/api/clinicas/${clinicaId}/horarios`).then(r => r.data).catch(() => []),
    enabled: !!clinicaId,
    staleTime: 5 * 60_000,
  });

  const { data: stats } = useQuery({
    queryKey: ['stats'],
    queryFn: () => api.get('/api/stats').then(r => r.data),
  });

  const medicoColorMap = useMemo(() => {
    const map: Record<string, string> = {};
    (medicos as any[]).forEach((m: any, i: number) => {
      map[m.id] = PALETTE[i % PALETTE.length];
    });
    return map;
  }, [medicos]);

  const events = useMemo(() => {
    const citaEvents = (citas as any[])
      .filter(c => showCanceladas || c.estado !== 'cancelada')
      .filter(c => hiddenMedicos.size === 0 || !hiddenMedicos.has(c.medicoId))
      .flatMap(c => expandCita(c, rangeStart, rangeEnd).map(citaToEvent));

    const bloqueEvents = generateBloqueEvents(bloques as any[], rangeStart, rangeEnd)
      .filter(e => hiddenMedicos.size === 0 || !hiddenMedicos.has((e as any)._bloque?.medicoId));

    return [...citaEvents, ...bloqueEvents];
  }, [citas, bloques, rangeStart, rangeEnd, hiddenMedicos, showCanceladas]);

  /* ─── Mutations ─── */

  const createMutation = useMutation({
    mutationFn: (data: any) => api.post('/api/citas', data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['citas-agenda'] });
      qc.invalidateQueries({ queryKey: ['stats'] });
      toast.success('Cita creada');
      setFormOpen(false);
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al crear la cita')),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, ...data }: any) => api.patch(`/api/citas/${id}`, data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['citas-agenda'] });
      toast.success('Cita actualizada');
      setSelectedCita(null);
      setEditingCita(null);
      setFormOpen(false);
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al actualizar')),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/citas/${id}`).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['citas-agenda'] });
      qc.invalidateQueries({ queryKey: ['stats'] });
      toast.success('Cita eliminada');
      setSelectedCita(null);
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al eliminar la cita')),
  });

  const iniciarMutation = useMutation({
    mutationFn: (cita: any) => api.patch(`/api/citas/${cita.id}`, { estado: 'en_curso' }).then(() => cita),
    onSuccess: (cita) => {
      qc.invalidateQueries({ queryKey: ['citas-agenda'] });
      navigate(`/consultas/${cita.id}`);
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al iniciar la consulta')),
  });

  /* ─── Calendar handlers ─── */

  const handleEventCreate = useCallback(({ start, end }: { start: string; end: string }) => {
    setFormDefaults({
      fechaInicio: toLocalDT(new Date(start)),
      fechaFin:    toLocalDT(new Date(end)),
    });
    setFormOpen(true);
  }, []);

  const handleEventClick = useCallback((event: CalendarEvent) => {
    if ((event as any)._isBloqueHorario) return;
    setSelectedCita((event as any)._cita);
  }, []);

  const handleEventDrop = useCallback(({ id, start, end }: { id: string; start: string; end: string }) => {
    const queryKey = ['citas-agenda', rangeStart.toISOString(), rangeEnd.toISOString()];
    const previous = qc.getQueryData<any[]>(queryKey);

    qc.setQueryData<any[]>(queryKey, (old = []) =>
      old.map(c => (c.id === id ? { ...c, fechaInicio: start, fechaFin: end } : c)),
    );

    api.patch(`/api/citas/${id}`, { fechaInicio: start, fechaFin: end })
      .then(() => {
        qc.invalidateQueries({ queryKey: ['citas-agenda'] });
        toast.success('Cita reagendada');
      })
      .catch((err) => {
        qc.setQueryData(queryKey, previous);
        toast.error(getErrorMessage(err, 'No se pudo reagendar la cita'));
      });
  }, [qc, rangeStart, rangeEnd]);

  /* ─── Toggle helpers ─── */

  function toggleMedico(id: string) {
    setHiddenMedicos(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  /* ─── Sidebar content (reutilizado en desktop y sheet mobile) ─── */

  const sidebarContent = (
    <>
      <div className="border-b border-gray-100">
        <MiniCalendar date={currentDate} onDateChange={setCurrentDate} />
      </div>
      <div className="px-4 py-2.5 border-b border-gray-100 flex items-center justify-between">
        <div>
          <p className="text-[10px] font-semibold text-text-secondary uppercase tracking-widest leading-none mb-0.5">Hoy</p>
          <p className="text-2xl font-bold text-text-primary leading-none">
            {stats?.citasHoy ?? 0}
            <span className="text-xs font-normal text-text-secondary ml-1">citas</span>
          </p>
        </div>
      </div>
      {canManage && (medicos as any[]).length > 0 && (
        <div className="border-b border-gray-100">
          <p className="text-[10px] font-semibold text-text-secondary uppercase tracking-widest px-3 pt-3 pb-1">
            Médicos
          </p>
          {(medicos as any[]).map((m: any) => (
            <MedicoCard
              key={m.id}
              medico={m}
              color={medicoColorMap[m.id] ?? '#9ca3af'}
              visible={!hiddenMedicos.has(m.id)}
              onToggle={() => toggleMedico(m.id)}
              bloques={bloques as any[]}
              clinicaHorarios={clinicaHorarios as any[]}
            />
          ))}
          <div className="pb-1" />
        </div>
      )}
      <div className="px-3 py-3 border-b border-gray-100">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={showCanceladas}
            onChange={e => setShowCanceladas(e.target.checked)}
            className="w-3.5 h-3.5 rounded border-gray-300 text-primary-600 focus:ring-primary-300 flex-shrink-0"
          />
          <p className="text-xs text-text-secondary font-medium">Mostrar canceladas</p>
        </label>
      </div>
      <div className="mx-3 my-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
        <div className="flex items-start gap-2">
          <Lightbulb size={13} className="text-amber-500 flex-shrink-0 mt-0.5" />
          <p className="text-[10px] text-text-secondary leading-relaxed">
            Clic en un médico para ocultarlo · clic en un hueco del horario para agendar.
          </p>
        </div>
      </div>
    </>
  );

  /* ─── Render ─── */

  return (
    <div className="flex h-full overflow-hidden relative">
      {/* ── Left Sidebar — desktop only ── */}
      <aside className="hidden md:flex w-56 flex-shrink-0 bg-white border-r border-gray-100 flex-col overflow-y-auto">
        {sidebarContent}
      </aside>

      {/* ── Calendar ── */}
      <div className="flex-1 min-w-0 overflow-hidden">
        <Calendar
          events={events}
          isLoading={loadingCitas}
          readOnly={!canManage}
          view={view}
          onViewChange={setView}
          date={currentDate}
          onDateChange={setCurrentDate}
          onEventCreate={canManage ? handleEventCreate : undefined}
          onEventClick={handleEventClick}
          onEventDrop={canManage ? handleEventDrop : undefined}
          className="h-full"
          clinicaHorarios={clinicaHorarios as any[]}
          capaTareas={capaTareas}
        />
      </div>

      {/* ── FAB mobile: nueva cita ── */}
      {canManage && (
        <button
          onClick={() => setFormOpen(true)}
          className="md:hidden fixed bottom-20 right-4 z-30 w-14 h-14 rounded-full bg-primary-500 text-white shadow-lg flex items-center justify-center"
          style={{ boxShadow: '0 4px 16px rgba(14,133,191,0.4)' }}
          aria-label="Nueva cita"
        >
          <Clock size={22} strokeWidth={2} />
        </button>
      )}

      {/* ── Botón filtros mobile ── */}
      <button
        onClick={() => setMobileSidebarOpen(true)}
        className="md:hidden fixed bottom-20 right-20 z-30 w-12 h-12 rounded-full bg-white border border-gray-200 shadow text-gray-600 flex items-center justify-center"
        aria-label="Filtros de agenda"
      >
        <SlidersHorizontal size={18} />
      </button>

      {/* ── Sheet mobile: sidebar de filtros ── */}
      {mobileSidebarOpen && (
        <>
          <div
            className="md:hidden fixed inset-0 bg-black/40 z-50"
            onClick={() => setMobileSidebarOpen(false)}
          />
          <div className="md:hidden fixed inset-x-0 bottom-0 z-50 bg-white rounded-t-3xl overflow-hidden"
            style={{ maxHeight: '82vh' }}>
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 rounded-full bg-gray-200" />
            </div>
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
              <p className="text-base font-bold text-text-primary">Filtros de agenda</p>
              <button
                onClick={() => setMobileSidebarOpen(false)}
                className="p-1.5 rounded-full hover:bg-gray-100 text-gray-400 transition-colors"
              >
                <X size={18} />
              </button>
            </div>
            <div className="overflow-y-auto">
              {sidebarContent}
            </div>
          </div>
        </>
      )}

      {/* ── Dialogs ── */}
      <CitaFormDialog
        open={formOpen}
        onOpenChange={(open) => { setFormOpen(open); if (!open) setEditingCita(null); }}
        pacientes={pacientes as any[]}
        medicos={medicos as any[]}
        clinicas={clinicas as any[]}
        defaultValues={formDefaults}
        editingCita={editingCita}
        onSubmit={(data) => {
          if (data.id) {
            updateMutation.mutate(data);
          } else {
            createMutation.mutate(data);
          }
        }}
        isSubmitting={createMutation.isPending || updateMutation.isPending}
      />

      <CitaDetailDialog
        cita={selectedCita}
        onClose={() => setSelectedCita(null)}
        canIniciarConsulta={canIniciarConsulta}
        onEditar={(cita) => { setEditingCita(cita); setSelectedCita(null); setFormOpen(true); }}
        onConfirmar={(id) => updateMutation.mutate({ id, estado: 'confirmada' })}
        onNoAsistio={(id) => updateMutation.mutate({ id, estado: 'no_asistio' })}
        onReagendar={(id, fi, ff) => updateMutation.mutate({ id, fechaInicio: fi, fechaFin: ff })}
        onEliminar={(id) => deleteMutation.mutate(id)}
        onIniciar={(cita) => iniciarMutation.mutate(cita)}
        onVerConsulta={(cita) => navigate(`/consultas/${cita.id}`)}
        isUpdating={updateMutation.isPending}
        isIniciando={iniciarMutation.isPending}
        isEliminating={deleteMutation.isPending}
      />

      {modalTarea}
    </div>
  );
}
